/**
 * Email-gated download modal for the Vakuai marketing deck.
 *
 * Two-step flow:
 *   1. Form (work email + name/company/role) → POST /marketing/request-deck
 *   2. Success view → direct download via GET /marketing/deck?token=...
 *
 * Free-email providers (gmail/yahoo/hotmail/etc.) are rejected by the backend
 * with a friendly error message.
 */
import React, { useState } from "react";
import api from "@/lib/api";
import { X, DownloadSimple, CheckCircle, Warning } from "@phosphor-icons/react";

export default function DeckDownloadModal({ onClose }) {
  const [form, setForm] = useState({ email: "", name: "", company: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [token, setToken] = useState("");

  const apiBase = process.env.REACT_APP_BACKEND_URL || "";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/marketing/request-deck", form);
      setToken(data.download_token);
      // Auto-start the download so the user doesn't need to click again.
      window.location.href = `${apiBase}/api/marketing/deck?token=${data.download_token}`;
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't start your download. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[80] flex items-center justify-center p-6"
      onClick={onClose} data-testid="deck-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white text-gray-900 max-w-md w-full shadow-2xl">
        <div className="bg-[#0B1733] text-white p-6 flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#C9A84C] mb-2 flex items-center gap-1.5">
              <DownloadSimple size={12} weight="fill" /> Marketing Deck
            </div>
            <h3 className="text-2xl font-black tracking-tight">Get the full Vakuai deck</h3>
            <div className="text-[12px] text-white/70 mt-1">Watermarked PDF · 12 pages · read-only</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="deck-close"><X size={20} /></button>
        </div>

        {token ? (
          <div className="p-8 text-center" data-testid="deck-success">
            <CheckCircle size={42} weight="fill" className="text-emerald-500 mx-auto mb-3" />
            <div className="font-bold text-lg">Your download has started</div>
            <div className="text-sm text-gray-600 mt-2">
              If it didn't, <a href={`${apiBase}/api/marketing/deck?token=${token}`}
                className="text-[#0020C2] font-semibold underline">click here</a>.
            </div>
            <button onClick={onClose}
              className="mt-6 px-5 py-2 text-[11px] font-bold uppercase tracking-wider bg-[#0B1733] text-white">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-3">
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">Work email *</label>
              <input
                required type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                data-testid="deck-email"
                className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0020C2]"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                We don&apos;t accept personal addresses (Gmail, Yahoo, Hotmail, etc.).
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">Name</label>
                <input value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="deck-name"
                  className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0020C2]" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">Role</label>
                <input value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder="e.g., CFO"
                  data-testid="deck-role"
                  className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0020C2]" />
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase font-bold text-gray-500">Company</label>
              <input value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                data-testid="deck-company"
                className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#0020C2]" />
            </div>
            {err && (
              <div className="text-[12px] text-[#DC2626] font-semibold flex items-start gap-1.5" data-testid="deck-error">
                <Warning size={14} weight="fill" className="shrink-0 mt-0.5" /> {err}
              </div>
            )}
            <div className="pt-2 flex items-center gap-3">
              <button type="submit" disabled={busy}
                data-testid="deck-submit"
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#0020C2] text-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-wider disabled:opacity-60">
                <DownloadSimple size={12} weight="bold" /> {busy ? "Preparing…" : "Get the deck"}
              </button>
            </div>
            <div className="text-[10px] text-gray-400 pt-1">
              By submitting, you agree we may contact you about Vakuai. We do not share your details with third parties.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
